import React, { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as LucideIcons from 'lucide-react'
import { Trash2, AlertTriangle } from 'lucide-react'

export default function UserPreview({
  questions,
  answers,
  vote,
  submitText,
  clearAnswer,
  isAnswered
}) {
  const [drafts, setDrafts] = useState({})
  const textRefs = useRef({})

  // Support legacy camelCase icon names stored before the migration to PascalCase
  const legacyIconMap = {
    thumbsUp:   'ThumbsUp',
    thumbsDown: 'ThumbsDown',
    circle:     'Circle',
    minus:      'Minus'
  }

  const renderIcon = (icon, color, size = 22) => {
    if (!icon || icon === 'none') return null
    const resolvedName = legacyIconMap[icon] ?? icon
    const Comp = LucideIcons[resolvedName]
    if (Comp)
      return (
        <Comp
          size={size}
          className="shrink-0"
          style={{ color: color || '#374151' }}
        />
      )
    return null
  }

  const autoResize = qid => {
    const el = textRefs.current[qid]
    if (!el) return
    el.style.height = 'auto'
    el.style.height = el.scrollHeight + 'px'
  }

  /* ---------------- CONDITIONAL ENGINE ---------------- */

  const shouldShow = q => {
    if (!q.condition) return true
    const parentAnswer = answers[q.condition.questionId]
    if (!parentAnswer) return false
    return parentAnswer.optionId === q.condition.optionId
  }

  const visibleQuestions = questions.filter(shouldShow)

  /* ---------------------------------------------------- */

  return (
    <div className="rounded-3xl border-4 border-orange-500 bg-white p-8">
      <h2 className="mb-8 text-xl font-bold text-orange-600">
        User POV
      </h2>

      <AnimatePresence mode="popLayout">
        {visibleQuestions.map(q => {
          const answer = answers[q.id]

          const size = q.size || 'regular'
          const isXSmall = size === 'xsmall'
          const isSmall  = size === 'small' || isXSmall

          const cardPad    = isXSmall ? 'p-2'      : isSmall ? 'p-4'      : 'p-6'
          const titleSize  = isXSmall ? 'text-sm'  : isSmall ? 'text-base': 'text-lg'
          const titleMb    = isSmall  ? 'mb-3'     : 'mb-4'
          const voteGap    = isSmall  ? 'gap-3'    : 'gap-4'
          const voteBtnPad = isXSmall ? 'px-3 py-1': isSmall ? 'px-4 py-2': 'px-6 py-3'
          const voteBtnMin = isSmall  ? 'min-w-[88px]' : 'min-w-[100px]'
          const textareaPad = isSmall ? 'p-2.5'    : 'p-3'
          const saveBtnPad  = isSmall ? 'px-4 py-1.5' : 'px-5 py-2'

          /* ── Special compact design for conflict_of_interest ── */
          if (q.type === 'conflict_of_interest') {
            const isConflictDeclared = !!answer?.optionId
            return (
              <motion.div
                key={q.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                className="mb-4"
              >
                {isConflictDeclared ? (
                  /* Answered: red warning banner */
                  <div className="flex items-center gap-3 rounded-2xl border-2 border-red-400 bg-red-50 px-4 py-2.5">
                    <AlertTriangle size={16} className="shrink-0 text-red-500" />
                    <span className="flex-1 text-xs font-bold text-red-700 leading-snug">
                      Interessenkonflikt erklärt
                    </span>
                    <button
                      onClick={() => clearAnswer(q.id)}
                      className="flex items-center gap-1.5 rounded-xl border-2 border-red-400 bg-white px-3 py-1 text-xs font-bold text-red-600 hover:bg-red-100 transition shrink-0"
                    >
                      <Trash2 size={12} />
                      Löschen
                    </button>
                  </div>
                ) : (
                  /* Unanswered: amber banner with inline button */
                  <div className="flex items-center gap-3 rounded-2xl border-2 border-amber-300 bg-amber-50 px-4 py-2.5">
                    <AlertTriangle size={14} className="shrink-0 text-amber-500 opacity-60" />
                    <span className="flex-1 text-xs font-semibold text-gray-700 leading-snug min-w-0">
                      {q.title}
                    </span>
                    <div className="flex gap-2 shrink-0">
                      {q.options.map(o => (
                        <motion.button
                          key={o.id}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => vote(q.id, o.id)}
                          className="flex items-center gap-1 rounded-xl border-2 border-amber-400 bg-white px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-amber-100 transition"
                        >
                          {renderIcon(o.icon, o.iconColor, 12)}
                          <span>{o.label}</span>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )
          }

          /* ── Standard vote / text question ── */
          return (
            <motion.div
              key={q.id}
              layout
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className={`mb-8 rounded-3xl border-2 ${cardPad} !bg-[#f1efec]`}
            >
              <div className={`${titleMb} flex items-start justify-between gap-2`}>
                <h3 className={`${titleSize} font-bold text-black flex-1 text-center mt-3`}>
                  {q.title}
                  {q.required && (
                    <span className="ml-2 text-red-500">*</span>
                  )}
                </h3>
                {q.type === 'vote' && answer?.optionId && (
                  <button
                    onClick={() => clearAnswer(q.id)}
                    className="rounded-lg border border-gray-300 bg-white p-1.5 text-gray-400 hover:text-gray-600 hover:border-gray-400 transition shrink-0"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>

              {/* ------------------ VOTE ------------------ */}
              {q.type === 'vote' && (
                <>
                  {answer?.optionId ? (
                    <div className="flex items-center justify-center gap-3 min-w-0">
                      {renderIcon(
                        q.options.find(o => o.id === answer.optionId)?.icon,
                        q.options.find(o => o.id === answer.optionId)?.iconColor
                      )}
                      <span className={`whitespace-pre-wrap break-words overflow-hidden block min-w-0 font-bold ${
                        isSmall ? 'text-base' : 'text-lg'
                      }`}>
                        {q.options.find(o => o.id === answer.optionId)?.label}
                      </span>
                    </div>
                  ) : (
                    <div
                      className={
                        q.layout === 'inline'
                          ? `flex flex-wrap justify-center ${voteGap}`
                          : `flex flex-col ${voteGap}`
                      }
                    >
                      {q.options.map(o => (
                        <motion.button
                          key={o.id}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => vote(q.id, o.id)}
                          className={`flex items-center justify-center gap-3 rounded-2xl border-2 ${voteBtnPad} text-black ${voteBtnMin} bg-[#e5e7eb] hover:border-gray-400`}
                        >
                          {renderIcon(o.icon, o.iconColor)}
                          <span className="break-words">{o.label}</span>
                        </motion.button>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* ------------------ TEXT ------------------ */}
              {q.type === 'text' && (
                <>
                  {answer?.value ? (
                    <motion.div
                      layout
                      className={`rounded-2xl border-4 border-orange-500 bg-orange-100 px-3 ${
                        isSmall ? 'py-3' : 'py-4'
                      } overflow-hidden`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className={`${isSmall ? 'text-lg' : 'text-xl'} font-semibold text-orange-700`}>
                          Ihr Kommentar:
                        </span>
                        <button
                          onClick={() => clearAnswer(q.id)}
                          className="rounded-full border-4 border-red-400 p-2 text-red-600 bg-white hover:scale-105 transition shrink-0"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <span className="whitespace-pre-wrap break-words block min-w-0">
                        {answer.value}
                      </span>
                    </motion.div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <textarea
                        ref={el => (textRefs.current[q.id] = el)}
                        value={drafts[q.id] || ''}
                        onChange={e => {
                          setDrafts(d => ({ ...d, [q.id]: e.target.value }))
                          autoResize(q.id)
                        }}
                        rows={1}
                        className={`w-full resize-none overflow-hidden rounded-2xl border-4 border-orange-300 ${textareaPad} focus:outline-none`}
                        placeholder="Schreiben Sie Ihre Antwort hier..."
                      />
                      <button
                        onClick={() => submitText(q.id, drafts[q.id] || '')}
                        className={`self-end rounded-2xl border-4 border-orange-500 ${saveBtnPad} text-orange-600`}
                      >
                        Speichern
                      </button>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )
        })}
      </AnimatePresence>
    </div>
  )
}
